//  @flow

import React, {useEffect, useMemo, useState} from 'react';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  TextField,
  Typography,
  debounce,
  makeStyles,
} from '@material-ui/core';

import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import {colors} from '../../../../../assets/styles/colors';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';

import {Autocomplete} from '@material-ui/lab';

import {useTypedSelector} from '../../../../../store';
import {useBindedActions} from '../../../../../utils/globalStore';

import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {actions as ProductFilterActions} from '../../../duck';

import type {Theme} from '@material-ui/core';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {useProductVariants} from '../../../../variants/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    width: '100%',
    boxShadow: '0px 0.5px 2px 0px #60617029',
    padding: theme.spacing(1, 2, 2, 2),
    backgroundColor: '#FFFFFF',
    display: 'flex',
    gap: theme.spacing(1),
    borderRadius: 12,
    border: `1px solid ${colors.grey300}`,
    flexDirection: 'column',
  },
  divider: {
    backgroundColor: colors.grey300,
  },
  title: {
    fontWeight: 600,
    color: colors.grey900,
    fontSize: 16,
  },
  subtitle: {
    color: '#717190',
    fontSize: 14,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  switchLabel: {
    color: colors.grey700,
    fontSize: 14,
  },
  autocompleteWrap: {
    margin: theme.spacing(2, 0),
  },
  autocomplete: {
    maxWidth: '100%',
    width: '100%',
  },
  textField: {
    borderRadius: '20px!important',
  },
  tag: {
    background: '#FFFFFF',
    border: `1px solid ${colors.grey300}`,
  },
  saveButton: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  saveBtnWrap: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon color="primary" fontSize="small" />;

type CustomOption = {
  id: string,
  name: string,
};

export default function Variants() {
  const classes = useStyles();

  const variantOptions = useProductVariants();
  const hasProductFiltersFeature = useIsFeatureInTier('PRODUCT_FILTER');
  const [enabled, setEnabled] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<CustomOption[]>([]);

  const productFiltersSetting = useTypedSelector(
    state => state.productFilters.productFiltersSetting
  );
  const {saveProductFilterSetting} = useBindedActions(ProductFilterActions);

  const handleEnable = debounce(checked => {
    saveProductFilterSetting({
      productFilterSettingRequest: {
        productVariantOptionFilter: {
          enabled: checked,
          productVariantOptionIds: selectedOptions.map(o => o.id),
        },
      },
    });
  }, 300);

  const handleSave = () => {
    saveProductFilterSetting({
      productFilterSettingRequest: {
        productVariantOptionFilter: {
          enabled,
          productVariantOptionIds: selectedOptions.map(o => o.id),
        },
      },
    });
  };

  useEffect(() => {
    if (productFiltersSetting?.productVariantFilter) {
      const {enabled, spec} = productFiltersSetting.productVariantFilter;
      setEnabled(enabled);
      setSelectedOptions(
        spec.productVariantOptionIds?.map(id => {
          const variant = variantOptions?.find(el => el.id === id);
          return {name: variant?.label, id};
        })
      );
    }
  }, [productFiltersSetting, variantOptions]);

  const parsedVariantOptions: Array<CustomOption> = useMemo(() => {
    if (!variantOptions) return [];

    return variantOptions.map(o => ({name: o.label, id: o.id}));
  }, [variantOptions]);

  const action = (
    <FormControlLabel
      control={
        <IosSwitch
          disabled={!hasProductFiltersFeature}
          defaultChecked={enabled}
          checked={enabled}
          onChange={e => handleEnable(e.target.checked)}
        />
      }
      classes={{label: classes.switchLabel}}
      labelPlacement="start"
      label={enabled ? 'Activado' : 'Desactivado'}
    />
  );

  const handleChange = (_, values) => setSelectedOptions(values);

  return (
    <div className={classes.card}>
      <div className={classes.actions}>
        <Typography className={classes.title}>Variantes</Typography>
        {action}
      </div>
      <Typography className={classes.subtitle}>
        Selecciona los filtros que quieres mostrar en tus páginas de
        categoría/colecciones
      </Typography>

      <div className={classes.autocompleteWrap}>
        <Autocomplete
          disabled={!hasProductFiltersFeature}
          fullWidth
          freeSolo
          noOptionsText="No existen variantes"
          classes={{root: classes.autocomplete}}
          multiple
          id="varian-options-tags"
          value={selectedOptions}
          onChange={handleChange}
          options={parsedVariantOptions}
          disableCloseOnSelect
          renderTags={(tags, getTagProps) => (
            <>
              {tags.map((item, index) => {
                return (
                  <Chip
                    label={item.name}
                    key={item.id}
                    {...getTagProps(index)}
                    className={classes.tag}
                  />
                );
              })}
            </>
          )}
          getOptionSelected={(option, selectedValue) =>
            option.id === selectedValue.id
          }
          getOptionLabel={option => option.name}
          renderOption={(option, {selected}) => (
            <React.Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{marginRight: 8}}
                checked={selected}
              />
              {option.name}
            </React.Fragment>
          )}
          renderInput={params => (
            <TextField
              {...params}
              classes={{root: classes.textField}}
              fullWidth
              variant="outlined"
              label="Seleccionar variantes"
              placeholder="Seleccionar variantes"
            />
          )}
        />

        <div className={classes.saveBtnWrap}>
          <CustomButton
            disabled={!enabled || !hasProductFiltersFeature}
            variant="contained"
            className={classes.saveButton}
            onClick={handleSave}
          >
            Guardar cambios
          </CustomButton>
        </div>
      </div>
    </div>
  );
}

Variants.propTypes = {};
